import React, { useEffect } from 'react';
import { Button } from '@carbon/react';
import { Download } from '@carbon/react/icons';
import { useSetAtom } from 'jotai';
import { toastAtom } from './atoms';
import { useTypeCurveDetailExport } from './hooks';

interface ExportButtonsProps {
  tcPublicId: string;
  iconOnly?: boolean;
}

export const ExportButtons: React.FC<ExportButtonsProps> = ({
  tcPublicId,
  iconOnly = false,
}) => {
  const setToast = useSetAtom(toastAtom);
  const { startExport, isExporting, exportStatus } =
    useTypeCurveDetailExport(tcPublicId);

  // The workbook is built in a job and arrives over Pusher, so surface the result
  // when the export finishes rather than when the request returns.
  useEffect(() => {
    if (exportStatus.status === 'complete') {
      setToast({
        kind: 'success',
        title: 'Export Ready',
        subtitle: 'Your Type Curve export is ready — click Download.',
        timeout: 5000,
      });
    } else if (exportStatus.status === 'failed') {
      setToast({
        kind: 'error',
        title: 'Export Failed',
        subtitle: exportStatus.error || 'Failed to export Type Curve data',
        timeout: 5000,
      });
    }
  }, [exportStatus.status, exportStatus.error, setToast]);

  const handleExportData = () => {
    if (isExporting) return;
    startExport();
  };

  // Auto-download is disabled (popup-blocked from the Pusher callback), so once the
  // signed URL arrives, present it as a real link the user clicks to download.
  if (exportStatus.status === 'complete' && exportStatus.downloadUrl) {
    return (
      <Button
        as="a"
        href={exportStatus.downloadUrl}
        kind={iconOnly ? 'tertiary' : 'primary'}
        hasIconOnly={iconOnly}
        iconDescription="Download Type Curve Data"
        tooltipAlignment="center"
        tooltipPosition="bottom"
        renderIcon={Download}
        size="md"
      >
        {iconOnly ? null : 'Download Type Curve Data'}
      </Button>
    );
  }

  return (
    <Button
      kind={iconOnly ? 'tertiary' : 'primary'}
      hasIconOnly={iconOnly}
      iconDescription="Export Type Curve Data"
      tooltipAlignment="center"
      tooltipPosition="bottom"
      renderIcon={Download}
      onClick={handleExportData}
      disabled={isExporting}
      size="md"
    >
      {iconOnly ? null : 'Export Type Curve Data'}
    </Button>
  );
};
